import { useRef, useState } from 'react'
import type { MediaFolder } from '@/content'
import { uploadImage } from '@/content'
import { Icon } from '@/components/ui'
import styles from './fields.module.css'

interface ImageFieldProps {
  id: string
  value: string
  token: string
  /** Cloudinary subfolder this field's uploads are filed under. */
  folder: MediaFolder
  /** `document` swaps the preview and the accepted types for files. */
  kind?: 'image' | 'file'
  onChange: (next: string) => void
}

const ACCEPT = {
  image: 'image/png,image/jpeg,image/webp,image/avif,image/svg+xml,image/gif',
  file: 'application/pdf',
} as const

/**
 * A picture: shown, replaced by choosing a file, or pointed at a URL.
 *
 * The text box stays visible alongside the uploader on purpose. It shows
 * exactly which Cloudinary asset a field points at, and lets one be pasted
 * in directly — hiding the URL behind a file picker would make the value
 * invisible and an existing asset impossible to reuse in a second place.
 *
 * Handles documents as well as pictures: a CV is a file the owner replaces
 * as often as a photograph, and a second uploader doing the same job with a
 * different name would be one more thing to keep in step.
 *
 * The destination folder is not a choice here. It comes from the field being
 * edited, so the Cloudinary library organises itself.
 */
export const ImageField = ({
  id,
  value,
  token,
  folder,
  kind = 'image',
  onChange,
}: ImageFieldProps) => {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  const upload = async (file: File) => {
    setBusy(true)
    setError(null)
    setWarning(null)
    try {
      const result = await uploadImage(token, file, folder)
      onChange(result.url)
      // Stored, but the account will not serve it. Worth saying loudly: the
      // upload looks successful and the download is broken for everyone.
      if (result.warning) setWarning(result.warning)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Upload failed')
    } finally {
      setBusy(false)
      // Clearing lets the same file be chosen again after a failure.
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className={styles.image}>
      <div className={styles.imagePreview}>
        {value && kind === 'image' ? (
          <img src={value} alt="" />
        ) : (
          <span className={styles.imageEmpty}>
            <Icon name={kind === 'file' ? 'download' : 'imageSquare'} size={22} />
            {value ? 'File set' : kind === 'file' ? 'No file' : 'No image'}
          </span>
        )}
      </div>

      <div className={styles.imageControls}>
        <input
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={
            kind === 'file'
              ? 'Upload a PDF, or paste a link to one'
              : 'Upload an image, or paste a link to one'
          }
          className={styles.input}
          spellCheck={false}
        />

        <div className={styles.imageButtons}>
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={busy}
            className={styles.ghostButton}
          >
            <Icon name="download" size={14} />
            {busy ? 'Uploading…' : kind === 'file' ? 'Upload PDF' : 'Upload'}
          </button>

          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className={styles.ghostButton}
            >
              <Icon name="close" size={14} />
              Clear
            </button>
          )}
        </div>

        <input
          ref={input}
          type="file"
          accept={ACCEPT[kind]}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void upload(file)
          }}
        />

        <p className={styles.hint}>
          Uploads are filed under <code>{folder}</code>
        </p>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        {warning && (
          <p className={styles.warning} role="alert">
            {warning}
          </p>
        )}
      </div>
    </div>
  )
}
